<template>
    <div
        ref="containerRef"
        class="col-span-full"
        :class="shouldRender ? props.gridClasses : undefined"
        :style="placeholderStyle"
    >
        <ProductListingPage
            v-if="shouldRender"
            :products="props.products"
            :teaser="props.teaser"
            :page="props.page"
            :limit="props.limit"
            :loading="props.loading"
            :kevel-ad="props.kevelAd"
            :kevel-ad-loading="props.kevelAdLoading"
            :kevel-ad-target="props.kevelAdTarget"
        />
    </div>
</template>

<script setup lang="ts">
import type { ComponentPublicInstance } from 'vue'
import type { Schemas } from '#shopware'
import { useIntersectionObserver } from '@vueuse/core'

const props = defineProps<{
    products: Schemas['Product'][]
    teaser?: Schemas['A4gProductListingTeaser'][]
    page: number
    limit?: number
    loading?: boolean
    // Re-applied to this wrapper (rather than skipped) so its cards still form real
    // grid columns/gaps — the wrapper itself is a grid item (col-span-full) of the
    // outer listing grid, spanning its full width, so recomputing the same responsive
    // column classes here lines up pixel-for-pixel with the outer grid's own columns.
    gridClasses?: string
    kevelAd?: ParsedKevelAd | null
    kevelAdLoading?: boolean
    kevelAdTarget?: (el: Element | ComponentPublicInstance | null) => void
}>()

const containerRef = ref<HTMLElement | null>(null)
const shouldRender = ref(true)
const cachedHeight = ref<number | null>(null)

// Infinite-scroll listing pages are never unmounted today, so every product's DOM,
// component instances, and images stay resident for the whole session — the primary
// driver of the mobile Safari OOM crash after ~10-15 scrolled pages. Unmounting far
// pages and holding their measured height as a placeholder keeps scroll length/position
// stable while bounding how many pages are mounted at once; re-entering the rootMargin
// remounts from `products`, which the parent keeps cached (no refetch needed).
useIntersectionObserver(containerRef, ([entry]) => {
    if (!entry) return

    if (entry.isIntersecting) {
        shouldRender.value = true
        return
    }

    if (shouldRender.value) {
        cachedHeight.value = containerRef.value?.offsetHeight || cachedHeight.value
        shouldRender.value = false
    }
}, { rootMargin: '200% 0px 200% 0px' })

const placeholderStyle = computed(() => (
    !shouldRender.value && cachedHeight.value
        ? { minHeight: `${cachedHeight.value}px` }
        : undefined
))
</script>
